import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { NewTabLink } from '@gonfalon/launchpad-experimental/NewTabLink';
import { useMarketoForm } from '@gonfalon/marketo/useMarketoForm';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Dialog,
  DialogTrigger,
  FieldError,
  Form,
  Heading,
  IconButton,
  Input,
  Label,
  Modal,
  ModalOverlay,
  TextField,
} from '@launchpad-ui/components';
import cx from 'clsx';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import { trackSignUp } from '#utils/loginUtils';

import { STATSIG_MIGRATION_OFFER_MARKETO_FORM_ID, STATSIG_MIGRATION_OFFER_PROGRAM_ID } from './constants';

import bannerStyles from './StatsigMigrationOfferBanner.module.css';
import styles from './StatsigMigrationOfferClaimModal.module.css';

function RequiredAsterisk() {
  return <span aria-hidden="true"> *</span>;
}

const FormSchema = z.object({
  email: z.string().email('Enter a valid email address').min(1, 'Work email is required'),
  company: z.string().min(1, 'Company name is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  jobTitle: z.string().min(1, 'Job title is required'),
  phone: z.string().optional(),
});

type FormData = z.infer<typeof FormSchema>;

type StatsigMigrationOfferClaimModalProps = {
  onClaimLinkClick: () => void;
};

const SUCCESS_AUTO_CLOSE_MS = 3000;

export const StatsigMigrationOfferClaimModal = ({ onClaimLinkClick }: StatsigMigrationOfferClaimModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const isOpenRef = useRef(false);

  const {
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    register,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    mode: 'all',
    defaultValues: {
      email: '',
      company: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      phone: '',
    },
  });

  const resetModalState = useCallback(() => {
    reset();
    setIsSubmitting(false);
    setIsSuccess(false);
  }, [reset]);

  const onCloseModal = useCallback(() => {
    isOpenRef.current = false;
    resetModalState();
    setIsOpen(false);
  }, [resetModalState]);

  const onOpenModal = useCallback(() => {
    isOpenRef.current = true;
    resetModalState();
    setIsOpen(true);
  }, [resetModalState]);

  const { onSubmit: marketoSubmit } = useMarketoForm({
    formId: STATSIG_MIGRATION_OFFER_MARKETO_FORM_ID,
    onSuccess: () => {
      if (!isOpenRef.current) {
        return;
      }

      trackSignUp('Statsig Migration Offer Claim Form Submitted', {
        component: 'StatsigMigrationOfferBanner',
        url: window.location.pathname,
      });
      setIsSubmitting(false);
      setIsSuccess(true);
    },
  });

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    const timeoutId = setTimeout(onCloseModal, SUCCESS_AUTO_CLOSE_MS);

    return () => clearTimeout(timeoutId);
  }, [isSuccess, onCloseModal]);

  const handleOpenChange = (open: boolean) => {
    isOpenRef.current = open;
    setIsOpen(open);
    if (!open) {
      resetModalState();
    }
  };

  const onConfirmModal = () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const { email, company, firstName, lastName, jobTitle, phone } = getValues();

    marketoSubmit({
      Email: email,
      Company: company,
      FirstName: firstName,
      LastName: lastName,
      Title: jobTitle,
      Phone: phone,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      programID: STATSIG_MIGRATION_OFFER_PROGRAM_ID,
    });
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Button
        variant="minimal"
        className={bannerStyles.claimLink}
        onPress={() => {
          onClaimLinkClick();
          onOpenModal();
        }}
      >
        Claim the offer
      </Button>
      <ModalOverlay>
        <Modal>
          <Dialog aria-label="Migrate from Statsig for free">
            {() => (
              <>
                {isSuccess ? (
                  <>
                    <div slot="header">
                      <Heading slot="title">Migrate from Statsig for free</Heading>
                      <IconButton
                        slot="close"
                        aria-label="Close"
                        icon="cancel"
                        size="small"
                        variant="minimal"
                        onPress={onCloseModal}
                      />
                    </div>
                    <div slot="body">
                      <Alert status="success" className={styles.successMessage}>
                        An expert will be in touch as soon as possible.
                      </Alert>
                    </div>
                  </>
                ) : (
                  <Form className={styles.form} onSubmit={handleSubmit(onConfirmModal)}>
                    <div slot="header">
                      <Heading slot="title">Migrate from Statsig for free</Heading>
                      <IconButton
                        slot="close"
                        aria-label="Close"
                        icon="cancel"
                        size="small"
                        variant="minimal"
                        onPress={onCloseModal}
                      />
                    </div>
                    <div className={styles.formBody} slot="body">
                      <div className={styles.wide}>
                        <TextField aria-label="Work email" isInvalid={!!errors.email}>
                          <Label>
                            Work email
                            <RequiredAsterisk />
                          </Label>
                          <Input type="email" placeholder="myemail@email.com" {...register('email')} />
                          {errors.email && <FieldError>{errors.email.message}</FieldError>}
                        </TextField>
                      </div>
                      <div>
                        <TextField aria-label="Company name" isInvalid={!!errors.company}>
                          <Label>
                            Company name
                            <RequiredAsterisk />
                          </Label>
                          <Input type="text" {...register('company')} />
                          {errors.company && <FieldError>{errors.company.message}</FieldError>}
                        </TextField>
                      </div>
                      <div>
                        <TextField aria-label="First name" isInvalid={!!errors.firstName}>
                          <Label>
                            First name
                            <RequiredAsterisk />
                          </Label>
                          <Input type="text" {...register('firstName')} />
                          {errors.firstName && <FieldError>{errors.firstName.message}</FieldError>}
                        </TextField>
                      </div>
                      <div>
                        <TextField aria-label="Last name" isInvalid={!!errors.lastName}>
                          <Label>
                            Last name
                            <RequiredAsterisk />
                          </Label>
                          <Input type="text" {...register('lastName')} />
                          {errors.lastName && <FieldError>{errors.lastName.message}</FieldError>}
                        </TextField>
                      </div>
                      <div>
                        <TextField aria-label="Job title" isInvalid={!!errors.jobTitle}>
                          <Label>
                            Job title
                            <RequiredAsterisk />
                          </Label>
                          <Input type="text" {...register('jobTitle')} />
                          {errors.jobTitle && <FieldError>{errors.jobTitle.message}</FieldError>}
                        </TextField>
                      </div>
                      <div>
                        <TextField aria-label="Phone number">
                          <Label>Phone number</Label>
                          <Input type="tel" {...register('phone')} />
                        </TextField>
                      </div>
                      <div className={cx(styles.wide, styles.disclaimer)}>
                        By submitting, I authorize LaunchDarkly to contact me with personalized marketing communications
                        about our products and services. See our{' '}
                        <NewTabLink
                          href="https://launchdarkly.com/policies/privacy/"
                          text="Privacy Policy"
                          showIcon={false}
                        />{' '}
                        or{' '}
                        <NewTabLink
                          href="https://go.launchdarkly.com/UnsubscribePage.html"
                          text="Opt-Out"
                          showIcon={false}
                        />{' '}
                        at any time.
                      </div>
                    </div>
                    <div slot="footer">
                      <Button onPress={onCloseModal}>Cancel</Button>
                      <Button
                        variant="primary"
                        type="submit"
                        isDisabled={!isValid || isSubmitting}
                        isPending={isSubmitting}
                      >
                        Submit
                      </Button>
                    </div>
                  </Form>
                )}
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
